import { useDialog } from "@/utils/dialog";
import { FONT_FAMILIES, FONT_SIZES } from "@/utils/fonts";
import { type Editor } from "@tiptap/core";
import { type Level } from "@tiptap/extension-heading";

import { ToolbarGroup, ToolbarMode, type Action } from "./types";

export type UseToolbarActionsReturn = {
  actions: Action[];
  formatButtons: ComputedRef<Action[]>;
  formatAdvancedButtons: ComputedRef<Action[]>;
  undoRedoButtons: ComputedRef<Action[]>;
  listButtons: ComputedRef<Action[]>;
  alignButtons: ComputedRef<Action[]>;
  linkButtons: ComputedRef<Action[]>;
  advancedButtons: ComputedRef<Action[]>;
  tableButtons: ComputedRef<Action[]>;
  embedButtons: ComputedRef<Action[]>;
  matchToolbarMode: (toolbarMode: ToolbarMode) => boolean;
  currentTextAlign: ComputedRef<Action | undefined>;
  currentFormatAdvanced: ComputedRef<Action | undefined>;
  changeTextAlign: (textAlign: string) => void;
  changeBlockType: (blockType: string) => void;
  changeFontSize: (value: number) => void;
  currentFontSize: ComputedRef<number | null>;
  currentFontFamilyLabel: ComputedRef<string>;
  changeFontFamily: (fontFamily: string) => void;
  incrementFontSize: () => void;
  decrementFontSize: () => void;
  insertCharacter: (character: string) => void;
  isSpecialCharactersModalOpen: Ref<boolean>;
};

export default function useToolbarActions(
  editor: Editor,
  groups?: ToolbarGroup[],
  disabledToolbarGroups?: string[]
): UseToolbarActionsReturn {
  const { t } = useI18n();
  const { openPrompt } = useDialog();
  const isSpecialCharactersModalOpen = ref(false);

  const toggleLink = async () => {
    const previousUrl = editor.getAttributes("link").href;

    const url = await openPrompt("URL", "url", t("buttons.save"), {
      inputPlaceholder: "Beispiel: https://example.com",
      inputValue: previousUrl,
      cancelButtonText: t("buttons.cancel"),
      inputValidator: (value: string) => {
        try {
          new URL(value);
          return false;
        } catch (error) {
          return t("errors.invalidUrl");
        }
      },
    });

    // cancelled
    if (url === null) {
      return;
    }

    // empty
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();

      return;
    }

    // update link
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  };

  const toggleIFrame = async () => {
    const previousUrl = editor.getAttributes("src").href;

    const url = await openPrompt("URL", "url", t("buttons.save"), {
      inputPlaceholder: "Beispiel: https://example.com",
      inputValue: previousUrl,
      cancelButtonText: t("buttons.cancel"),
      inputValidator: (value: string) => {
        try {
          new URL(value);
          return false;
        } catch (error) {
          return t("errors.invalidUrl");
        }
      },
    });

    // cancelled
    if (!url || url === "") {
      return;
    }

    // https://www.youtube.com/embed/9bZkp7q19f0
    editor.chain().focus().setIframe({ src: url }).run();
  };

  const actions: Action[] = [
    {
      action: () => editor.chain().focus().toggleHeading({ level: 1 }).run(),
      isActive: () => editor.isActive("heading", { level: 1 }),
      icon: "type-h1",
      group: "header",
      enabled: true,
      toolbarMode: "full",
      mobileEnabled: false,
      label: "wysiwyg.actions.h1",
    },
    {
      action: () => editor.chain().focus().toggleHeading({ level: 2 }).run(),
      isActive: () => editor.isActive("heading", { level: 2 }),
      icon: "type-h2",
      group: "header",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
      label: "wysiwyg.actions.h2",
    },
    {
      action: () => editor.chain().focus().toggleHeading({ level: 3 }).run(),
      isActive: () => editor.isActive("heading", { level: 3 }),
      icon: "type-h3",
      group: "header",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
      label: "wysiwyg.actions.h3",
    },
    {
      action: () => editor.chain().focus().setParagraph().run(),
      isActive: () => editor.isActive("paragraph"),
      icon: "paragraph",
      group: "header",
      label: "wysiwyg.blockTypes.paragraph",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().toggleBold().run(),
      isActive: () => editor.isActive("bold"),
      icon: "type-bold",
      group: "format",
      label: "wysiwyg.actions.bold",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().toggleItalic().run(),
      isActive: () => editor.isActive("italic"),
      icon: "type-italic",
      group: "format",
      label: "wysiwyg.actions.italic",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().toggleUnderline().run(),
      isActive: () => editor.isActive("underline"),
      icon: "type-underline",
      group: "format",
      label: "wysiwyg.actions.underline",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().toggleStrike().run(),
      isActive: () => editor.isActive("strike"),
      icon: "type-strikethrough",
      group: "formatAdvanced",
      label: "wysiwyg.actions.strikethrough",
      enabled: true,
      toolbarMode: "md",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().toggleCode().run(),
      isActive: () => editor.isActive("code"),
      icon: "code",
      group: "formatAdvanced",
      label: "wysiwyg.actions.code",
      enabled: true,
      toolbarMode: "md",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().toggleHighlight().run(),
      isActive: () => editor.isActive("highlight"),
      icon: "pen",
      group: "formatAdvanced",
      label: "wysiwyg.actions.highlight",
      enabled: true,
      toolbarMode: "md",
      mobileEnabled: true,
    },
    {
      action: () =>
        editor.chain().focus().addKatexInline({ content: "" }).run(),
      isActive: () => false,
      icon: "braces-asterisk",
      group: "formatAdvanced",
      label: "wysiwyg.actions.katex",
      enabled: true,
      toolbarMode: "md",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().undo().run(),
      isActive: () => true,
      icon: "arrow-counterclockwise",
      group: "undoRedo",
      label: "wysiwyg.actions.undo",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().redo().run(),
      isActive: () => true,
      icon: "arrow-clockwise",
      group: "undoRedo",
      label: "wysiwyg.actions.redo",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().toggleBulletList().run(),
      isActive: () => editor.isActive("bulletList"),
      icon: "list-ul",
      group: "list",
      label: "wysiwyg.actions.bulletList",
      enabled: true,
      toolbarMode: "md",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().toggleOrderedList().run(),
      isActive: () => editor.isActive("orderedList"),
      icon: "list-ol",
      group: "list",
      label: "wysiwyg.actions.orderedList",
      enabled: true,
      toolbarMode: "md",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().setTextAlign("left").run(),
      isActive: () => editor.isActive({ textAlign: "left" }),
      icon: "text-left",
      group: "align",
      label: "wysiwyg.actions.alignLeft",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().setTextAlign("center").run(),
      isActive: () => editor.isActive({ textAlign: "center" }),
      icon: "text-center",
      group: "align",
      label: "wysiwyg.actions.alignCenter",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().setTextAlign("right").run(),
      isActive: () => editor.isActive({ textAlign: "right" }),
      icon: "text-right",
      group: "align",
      label: "wysiwyg.actions.alignRight",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: false,
    },
    {
      action: () => toggleLink(),
      isActive: () => editor.isActive("link"),
      icon: "link-45deg",
      group: "link",
      label: "wysiwyg.actions.link",
      enabled: true,
      toolbarMode: "sm",
      mobileEnabled: true,
    },
    {
      action: () => editor.chain().focus().toggleBlockquote().run(),
      isActive: () => editor.isActive("blockquote"),
      icon: "quote",
      label: "wysiwyg.actions.blockquote",
      group: "advanced",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().toggleCodeBlock().run(),
      isActive: () => editor.isActive("codeBlock"),
      icon: "code",
      label: "wysiwyg.actions.codeBlock",
      group: "advanced",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
    {
      action: () => (isSpecialCharactersModalOpen.value = true),
      isActive: () => true,
      icon: "hash",
      label: "wysiwyg.actions.specialCharacters",
      group: "advanced",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
    {
      action: () => editor.chain().focus().setHorizontalRule().run(),
      isActive: () => editor.isActive("horizontalRule"),
      icon: "hr",
      label: "wysiwyg.actions.horizontalRule",
      group: "advanced",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
    {
      action: () =>
        editor
          .chain()
          .focus()
          .insertTable({ rows: 3, cols: 3, withHeaderRow: false })
          .run(),
      isActive: () => editor.isActive("table"),
      icon: "table",
      label: "wysiwyg.actions.table",
      group: "table",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
    {
      action: () => toggleIFrame(),
      isActive: () => editor.isActive("iframe"),
      icon: "code-square",
      label: "wysiwyg.actions.iframe",
      group: "embed",
      enabled: true,
      toolbarMode: "full",
      mobileEnabled: false,
    },
    {
      action: () =>
        editor.chain().focus().addKatex({ content: "x^2 + y^2 = z^2" }).run(),
      isActive: () => editor.isActive("formula"),
      icon: "braces-asterisk",
      label: "wysiwyg.actions.katexBlock",
      group: "advanced",
      enabled: true,
      toolbarMode: "lg",
      mobileEnabled: false,
    },
  ];

  const matchToolbarMode = (toolbarMode: ToolbarMode): boolean => {
    if (toolbarMode === "full") {
      return toolbarMode === "full";
    } else if (toolbarMode === "lg") {
      return toolbarMode === "lg" || toolbarMode === "full";
    } else if (toolbarMode === "md") {
      return (
        toolbarMode === "md" || toolbarMode === "lg" || toolbarMode === "full"
      );
    } else if (toolbarMode === "sm") {
      return (
        toolbarMode === "sm" ||
        toolbarMode === "md" ||
        toolbarMode === "lg" ||
        toolbarMode === "full"
      );
    } else {
      throw new Error(`Invalid toolbar mode ${toolbarMode}`);
    }
  };

  const inDisabledToolbarGroups = (group: string): boolean => {
    if (disabledToolbarGroups) {
      return disabledToolbarGroups.includes(group);
    } else {
      return true;
    }
  };

  const enabledActions = computed(() =>
    actions
      .filter((action) => action.enabled)
      .filter((action) => matchToolbarMode(action.toolbarMode))
      .filter((action) => !inDisabledToolbarGroups(action.group))
  );

  const formatButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "format");
  });

  const formatAdvancedButtons = computed(() => {
    return enabledActions.value.filter(
      (action) => action.group === "formatAdvanced"
    );
  });

  const undoRedoButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "undoRedo");
  });

  const listButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "list");
  });

  const alignButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "align");
  });

  const linkButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "link");
  });

  const advancedButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "advanced");
  });

  const tableButtons = computed(() => {
    return enabledActions.value.filter((action) => action.group === "table");
  });

  const embedButtons = computed(() => {
    return (
      (groups?.includes("embed") &&
        enabledActions.value.filter((action) => action.group === "embed")) ||
      []
    );
  });

  const TEXT_ALIGNS = ["left", "center", "right"];

  const changeTextAlign = (textAlign: string) => {
    editor.chain().focus().setTextAlign(textAlign).run();
  };

  const currentTextAlign = computed(() => {
    const match = TEXT_ALIGNS.find((textAlign) => {
      return editor.isActive({ textAlign });
    });

    if (match) {
      return alignButtons.value.find((button) => {
        return button.value === match;
      });
    }

    return alignButtons.value[0];
  });

  const currentFormatAdvanced = computed(() => {
    const match = formatAdvancedButtons.value.find((button) => {
      return button.isActive();
    });

    if (match) {
      return match;
    }

    return formatAdvancedButtons.value[0];
  });

  const changeBlockType = (blockType: string) => {
    if (blockType === "p") {
      editor.chain().focus().setParagraph().run();
    } else if (blockType.startsWith("h")) {
      const level = parseInt(blockType[1]) as Level;
      editor.chain().focus().toggleHeading({ level }).run();
    }
  };

  const changeFontSize = (value: number) => {
    if (value === 16) {
      editor.chain().focus().unsetFontSize().run();
      return;
    }
    editor.chain().focus().setFontSize(`${value}px`).run();
  };

  const incrementFontSize = () => {
    const current = currentFontSize.value;
    if (current === null) {
      return;
    }
    const index = FONT_SIZES.findIndex((fontSize) => {
      return fontSize.value === current;
    });
    if (index === -1) {
      return;
    }
    const next = FONT_SIZES[index + 1];
    if (next) {
      changeFontSize(next.value);
    }
  };

  const decrementFontSize = () => {
    const current = currentFontSize.value;
    if (current === null) {
      return;
    }
    const index = FONT_SIZES.findIndex((fontSize) => {
      return fontSize.value === current;
    });
    if (index === -1) {
      return;
    }
    const prev = FONT_SIZES[index - 1];
    if (prev) {
      changeFontSize(prev.value);
    }
  };

  // font-size uses px, but FONT_SIZES has no unit
  const currentFontSize = computed(() => {
    const size = editor.getAttributes("textStyle").fontSize;
    const match = FONT_SIZES.find((fontSize) => {
      return size === `${fontSize.value}px`;
    });

    if (match) {
      return match.value;
    }

    if (editor.isActive("heading", { level: 1 })) {
      return 48;
    } else if (editor.isActive("heading", { level: 2 })) {
      return 36;
    } else if (editor.isActive("heading", { level: 3 })) {
      return 30;
    } else if (editor.isActive("paragraph")) {
      return 16;
    }
    return null;
  });

  const changeFontFamily = (fontFamily: string) => {
    editor.chain().focus().setFontFamily(fontFamily).run();
  };

  // font-family has no quoted strings, but FONT_FAMILIES uses quoted strings
  // we need to replace all the quotes for comparison
  const currentFontFamilyLabel = computed(() => {
    const currentFontFamily = editor.getAttributes("textStyle").fontFamily;
    const match = FONT_FAMILIES.find((fontFamily) => {
      return currentFontFamily === fontFamily.value.replaceAll('"', "");
    });
    if (match) {
      return match.label;
    }
    return FONT_FAMILIES[0].label;
  });

  const insertCharacter = (character: string) => {
    const cursorPosition = editor.state.selection.$anchor.pos;
    if (cursorPosition) {
      // insert
      editor.commands.insertContentAt(cursorPosition, character);
    } else {
      // append
      editor.commands.insertContent(character);
    }
  };

  return {
    actions,
    formatButtons,
    formatAdvancedButtons,
    undoRedoButtons,
    listButtons,
    alignButtons,
    linkButtons,
    advancedButtons,
    tableButtons,
    embedButtons,
    matchToolbarMode,
    currentTextAlign,
    currentFormatAdvanced,
    changeTextAlign,
    changeBlockType,
    changeFontSize,
    currentFontSize,
    currentFontFamilyLabel,
    changeFontFamily,
    incrementFontSize,
    decrementFontSize,
    insertCharacter,
    isSpecialCharactersModalOpen,
  };
}
